"use client"

import { useState, useEffect } from "react"
import { auth, db, storage, ref, set, storageRef, uploadBytes, getDownloadURL, get } from "../Firebase/firebase-config"
import { getDatabase } from "firebase/database"
import "./Finance.css"

function Finance() {
  // Fetch company name from profile dashboard
  const [CompanyName, setCompanyName] = useState("")

  useEffect(() => {
    const fetchCompanyProfile = async () => {
      try {
        const user = auth.currentUser
        if (user) {
          const profileRef = ref(db, `profileDashboard/${user.uid}`)
          const snapshot = await get(profileRef)
          if (snapshot.exists()) {
            const profileData = snapshot.val()
            setCompanyName(profileData.company_name || "")
          } else {
          }
        }
      } catch (error) {
        console.error("Error fetching company profile:", error)
      }
    }

    fetchCompanyProfile()
  }, [])

  const getIndianTimestamp = () => {
    const now = new Date()
    const istOffset = 5.5 * 60 * 60 * 1000 // IST is UTC+5:30
    const istTime = new Date(now.getTime() + istOffset)
    return istTime.toISOString().replace("T", " ").replace("Z", "")
  }

  const gifUrl = "/Images/New notification.gif"

  const sendNotification = async (userId, applicationId, CompanyName) => {
    const db = getDatabase()
    const notificationRef = ref(db, `supplier_notifications/finance/${userId}/${applicationId}`)

    const newNotification = {
      title: "Finance",
      message: `A new finance request has been sent by ${CompanyName}. Please review the details.`,
      gifUrl,
      timestamp: getIndianTimestamp(),
      seen: false,
    }

    try {
      await set(notificationRef, newNotification)
    } catch (error) {
      console.error("Error sending notification:", error.message)
    }
  }

  // Flutter-style data structure
  const [applicationData, setApplicationData] = useState({
    personalInfo: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      pan: "",
      gstin: "",
      dateOfBirth: "",
      address: "",
    },
    financialDetails: {
      loanType: "Personal Loan",
      loanAmount: "",
      tenure: "12 months",
      monthlyIncome: "",
      employmentType: "Salaried",
      company: "",
    },
  })

  const [files, setFiles] = useState({
    aadhar: null,
    pan: null,
    salary: null,
    bank: null,
    employment: null,
  })

  const [documentUploaded, setDocumentUploaded] = useState({
    aadhar: false,
    pan: false,
    salary: false,
    bank: false,
    employment: false,
  })

  const [documentUrls, setDocumentUrls] = useState({
    aadhar: "",
    pan: "",
    salary: "",
    bank: "",
    employment: "",
  })

  const [successMessage, setSuccessMessage] = useState("")
  const [error, setError] = useState("")
  const [fileErrors, setFileErrors] = useState({
    aadhar: "",
    pan: "",
    salary: "",
    bank: "",
    employment: "",
  })

  const [selectedDate, setSelectedDate] = useState(null)
  const [isUploading, setIsUploading] = useState(false)

  // Handle input change for personal info
  const handlePersonalInfoChange = (e) => {
    const { name, value } = e.target

    if (name === "phone") {
      // Format phone number and limit to 10 digits
      const digitsOnly = value.replace(/\D/g, "").slice(0, 15)
      setApplicationData((prev) => ({
        ...prev,
        personalInfo: { ...prev.personalInfo, [name]: digitsOnly },
      }))
    } else if (name === "pan" || name === "gstin") {
      // Convert to uppercase for PAN and GSTIN
      setApplicationData((prev) => ({
        ...prev,
        personalInfo: { ...prev.personalInfo, [name]: value.toUpperCase() },
      }))
    } else {
      setApplicationData((prev) => ({
        ...prev,
        personalInfo: { ...prev.personalInfo, [name]: value },
      }))
    }

    // Auto-fill PAN from GSTIN
    if (name === "gstin" && value.length >= 10) {
      const extractedPan = value.substring(2, 12)
      if (isValidPan(extractedPan)) {
        setApplicationData((prev) => ({
          ...prev,
          personalInfo: { ...prev.personalInfo, pan: extractedPan },
        }))
      }
    }
  }

  // Handle input change for financial details
  const handleFinancialDetailsChange = (e) => {
    const { name, value } = e.target
    setApplicationData((prev) => ({
      ...prev,
      financialDetails: { ...prev.financialDetails, [name]: value },
    }))
  }

  const isValidPan = (pan) => {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/
    return panRegex.test(pan)
  }

  const handleDateChange = (e) => {
    const date = new Date(e.target.value)
    setSelectedDate(date)
    const formattedDate = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`
    setApplicationData((prev) => ({
      ...prev,
      personalInfo: { ...prev.personalInfo, dateOfBirth: formattedDate },
    }))
  }

  const handleFileChange = async (e, fileType) => {
    const file = e.target.files[0]
    const maxSize = 5 * 1024 * 1024 // 5MB

    // Reset error for this file type
    setFileErrors((prev) => ({ ...prev, [fileType]: "" }))

    if (!file) {
      return
    }

    if (file.size > maxSize) {
      setFileErrors((prev) => ({
        ...prev,
        [fileType]: `File size should be below 5MB. Current size: ${(file.size / (1024 * 1024)).toFixed(1)}MB`,
      }))
      e.target.value = null
      return
    }

    // Update files state and immediately upload
    setFiles((prev) => ({ ...prev, [fileType]: file }))

    // Upload immediately when file is selected
    try {
      setIsUploading(true)
      await uploadDocument(fileType, file)
    } catch (error) {
      setFileErrors((prev) => ({ ...prev, [fileType]: error.message }))
    } finally {
      setIsUploading(false)
    }
  }

  const uploadDocument = async (docType, file) => {
    try {
      const user = auth.currentUser
      if (!user) throw new Error("User not authenticated")

      const fileName = `${docType}_${Date.now()}.${file.name.split(".").pop()}`
      const fileRef = storageRef(storage, `Finance/${user.uid}/${fileName}`)

      const snapshot = await uploadBytes(fileRef, file)
      const downloadUrl = await getDownloadURL(snapshot.ref)

      // Update both uploaded status and URLs
      setDocumentUploaded((prev) => ({ ...prev, [docType]: true }))
      setDocumentUrls((prev) => ({ ...prev, [docType]: downloadUrl }))

      return downloadUrl
    } catch (error) {
      console.error(`Failed to upload ${docType}:`, error)
      throw new Error(`Failed to upload ${docType}: ${error.message}`)
    }
  }

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const isApplicationEmpty = () => {
    const { personalInfo, financialDetails } = applicationData
    return (
      !personalInfo.firstName.trim() ||
      !personalInfo.lastName.trim() ||
      !personalInfo.email.trim() ||
      !personalInfo.phone.trim() ||
      !personalInfo.pan.trim() ||
      !financialDetails.loanAmount.trim() ||
      !financialDetails.monthlyIncome.trim() ||
      !financialDetails.company.trim()
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")

    // Validate required fields
    if (isApplicationEmpty()) {
      setError("Please fill all required fields before submitting")
      return
    }

    // Validate email format
    if (!validateEmail(applicationData.personalInfo.email)) {
      setError("Please enter a valid email address")
      return
    }

    // Validate phone number
    if (
      applicationData.personalInfo.phone.length < 10 ||
      applicationData.personalInfo.phone.length > 15
    ) {
      setError("Please enter a valid phone number (10 to 15 digits)");
      return;
    }


    // Validate PAN if provided
    if (applicationData.personalInfo.pan && !isValidPan(applicationData.personalInfo.pan)) {
      setError("Invalid PAN number format")
      return
    }

    try {
      const user = auth.currentUser
      if (!user) {
        setError("User not authenticated")
        return
      }

      // Generate application ID like Flutter
      const applicationId = `LA${Date.now().toString().substring(8)}`

      // Format timestamp like Flutter
      const timestamp = new Date()
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
           hour12: true,
        })
        .replace(",", "")

      // Create document status based on current documentUrls state
      const documentStatus = {}
      Object.keys(documentUrls).forEach((key) => {
        documentStatus[key] = documentUrls[key] && documentUrls[key].trim() !== "" ? "yes" : "no"
      })
      // Structure data like Flutter
      const finalApplicationData = {
        applicationId,
        timestamp,
        personalInfo: {
          ...applicationData.personalInfo,
          company: CompanyName,
        },
        financialDetails: applicationData.financialDetails,
        documents: documentStatus,
        status: "submitted",
      }

      // console.log("Final application data:", finalApplicationData)

      // Save under Finance/userId/applicationId (like Flutter)
      const applicationRef = ref(db, `Finance/${user.uid}/${applicationId}`)
      await set(applicationRef, finalApplicationData)

      // Send notification
      await sendNotification(user.uid, applicationId, CompanyName)

      // Reset form
      setApplicationData({
        personalInfo: {
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          pan: "",
          gstin: "",
          dateOfBirth: "",
          address: "",
        },
        financialDetails: {
          loanType: "Personal Loan",
          loanAmount: "",
          tenure: "12 months",
          monthlyIncome: "",
          employmentType: "Salaried",
          company: "",
        },
      })

      setFiles({
        aadhar: null,
        pan: null,
        salary: null,
        bank: null,
        employment: null,
      })

      setDocumentUploaded({
        aadhar: false,
        pan: false,
        salary: false,
        bank: false,
        employment: false,
      })

      setDocumentUrls({
        aadhar: "",
        pan: "",
        salary: "",
        bank: "",
        employment: "",
      })

      setSelectedDate(null)

      // Clear file inputs
      const fileInputs = document.querySelectorAll('input[type="file"]')
      fileInputs.forEach((input) => {
        input.value = ""
      })

      setSuccessMessage(`Loan application submitted successfully! Application ID: ${applicationId}`)
      setTimeout(() => setSuccessMessage(""), 8000)
    } catch (error) {
      setError("Submission failed: " + error.message)
      console.error(error)
    }
  }

  return (
    <div className="finance-container">
      <div className="loan-form-container">
        <h1 className="title">Loan Application Form</h1>
        <form className="loan-form" onSubmit={handleSubmit}>
          {/* Personal Information Section */}
          <div className="section-header">
            <h2>Personal Information</h2>
          </div>

          <div className="half-width">
            <label>
              First Name: <span className="required">*</span>
            </label>
            <input
              type="text"
              name="firstName"
              value={applicationData.personalInfo.firstName}
              onChange={handlePersonalInfoChange}
              required
              maxLength="50"
              placeholder="Enter your first name"
            />
          </div>

          <div className="half-width">
            <label>
              Last Name: <span className="required">*</span>
            </label>
            <input
              type="text"
              name="lastName"
              value={applicationData.personalInfo.lastName}
              onChange={handlePersonalInfoChange}
              required
              maxLength="50"
              placeholder="Enter your last name"
            />
          </div>

          <div className="half-width">
            <label>
              Email Address: <span className="required">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={applicationData.personalInfo.email}
              onChange={handlePersonalInfoChange}
              required
              placeholder="example@domain.com"
            />
          </div>

          <div className="half-width">
            <label>
              Phone Number: <span className="required">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={applicationData.personalInfo.phone}
              onChange={handlePersonalInfoChange}
              required
              placeholder="Enter mobile number"
              minLength={10}
              maxLength={15}
            />
          </div>

          <div className="half-width">
            <label>GSTIN Number (Optional):</label>
            <input
              type="text"
              name="gstin"
              value={applicationData.personalInfo.gstin}
              placeholder="Auto-fills PAN from GSTIN"
              onChange={handlePersonalInfoChange}
              maxLength="15"
            />
          </div>

          <div className="half-width">
            <label>
              PAN Number: <span className="required">*</span>
            </label>
            <input
              type="text"
              name="pan"
              value={applicationData.personalInfo.pan}
              placeholder="ABCDE1234F"
              onChange={handlePersonalInfoChange}
              required
              maxLength="10"
            />
          </div>

          <div className="half-width">
            <label>Date of Birth:</label>
            <input
              type="date"
              onChange={handleDateChange}
              max={new Date(Date.now() - 18 * 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]}
            />
          </div>

          <div className="half-width">
            <label>Address:</label>
            <input
              type="text"
              name="address"
              value={applicationData.personalInfo.address}
              onChange={handlePersonalInfoChange}
              minLength={5}
              maxLength={200}
              placeholder="Complete address"
            />
            <div className="char-counter">
              {applicationData.personalInfo.address.replace(/\s/g, "").length} / 200 characters
            </div>
          </div>

          {/* Financial Details Section */}
          <div className="section-header">
            <h2>Financial Details</h2>
          </div>

          <div className="half-width">
            <label>
              Loan Type: <span className="required">*</span>
            </label>
            <select
              name="loanType"
              value={applicationData.financialDetails.loanType}
              onChange={handleFinancialDetailsChange}
              required
            >
              <option value="Personal Loan">Personal Loan</option>
              <option value="Business Loan">Business Loan</option>
              <option value="Home Loan">Home Loan</option>
              <option value="Car Loan">Car Loan</option>
            </select>
          </div>

          <div className="half-width">
            <label>
              Loan Amount (₹): <span className="required">*</span>
            </label>
            <input
              type="number"
              name="loanAmount"
              value={applicationData.financialDetails.loanAmount}
              onChange={handleFinancialDetailsChange}
              onWheel={(e) => e.target.blur()}
              required
              min="10000"
              max="10000000"
              step="1000"
              placeholder="Enter amount in ₹"
            />
          </div>

          <div className="half-width">
            <label>
              Tenure: <span className="required">*</span>
            </label>
            <select
              name="tenure"
              value={applicationData.financialDetails.tenure}
              onChange={handleFinancialDetailsChange}
              required
            >
              <option value="6 months">6 months</option>
              <option value="12 months">12 months</option>
              <option value="24 months">24 months</option>
              <option value="36 months">36 months</option>
              <option value="60 months">60 months</option>
            </select>
          </div>

          <div className="half-width">
            <label>
              Monthly Income (₹): <span className="required">*</span>
            </label>
            <input
              type="number"
              name="monthlyIncome"
              value={applicationData.financialDetails.monthlyIncome}
              onChange={handleFinancialDetailsChange}
              onWheel={(e) => e.target.blur()}
              min="0"
              max="100000000"
              required
              placeholder="Enter monthly income"
            />
          </div>

          <div className="half-width">
            <label>
              Employment Type: <span className="required">*</span>
            </label>
            <select
              name="employmentType"
              value={applicationData.financialDetails.employmentType}
              onChange={handleFinancialDetailsChange}
              required
            >
              <option value="Salaried">Salaried</option>
              <option value="Self Employed">Self Employed</option>
              <option value="Business Owner">Business Owner</option>
              <option value="Freelancer">Freelancer</option>
            </select>
          </div>

          <div className="half-width">
            <label>
              Company/Organization: <span className="required">*</span>
            </label>
            <input
              type="text"
              name="company"
              value={applicationData.financialDetails.company}
              onChange={handleFinancialDetailsChange}
              required
              placeholder="Company name"
            />
          </div>

          {/* Document Upload Section */}
          <div className="section-header">
            <h2>Document Upload</h2>
            <p className="file-info">Files should be below 5MB (PDF/JPEG/PNG)</p>
          </div>

          <div className="file-upload-group">
            <label>
              Aadhar Card: <span className="required"></span>
              <span className="file-hint">Max 5MB</span>
            </label>
            <input
              type="file"
              accept=".pdf,image/jpeg,image/png"
              onChange={(e) => handleFileChange(e, "aadhar")}
              className={fileErrors.aadhar ? "file-error" : ""}
              disabled={isUploading}
            />
            {fileErrors.aadhar && <div className="field-error">{fileErrors.aadhar}</div>}
            {documentUploaded.aadhar && <div className="upload-success">✓ Uploaded successfully</div>}
          </div>

          <div className="file-upload-group">
            <label>
              PAN Card:
              <span className="file-hint">Max 5MB</span>
            </label>
            <input
              type="file"
              accept=".pdf,image/jpeg,image/png"
              onChange={(e) => handleFileChange(e, "pan")}
              className={fileErrors.pan ? "file-error" : ""}
              disabled={isUploading}
            />
            {fileErrors.pan && <div className="field-error">{fileErrors.pan}</div>}
            {documentUploaded.pan && <div className="upload-success">✓ Uploaded successfully</div>}
          </div>

          <div className="file-upload-group">
            <label>
              Salary Slips (3 months):
              <span className="file-hint">Max 5MB</span>
            </label>
            <input
              type="file"
              accept=".pdf,image/jpeg,image/png"
              onChange={(e) => handleFileChange(e, "salary")}
              className={fileErrors.salary ? "file-error" : ""}
              disabled={isUploading}
            />
            {fileErrors.salary && <div className="field-error">{fileErrors.salary}</div>}
            {documentUploaded.salary && <div className="upload-success">✓ Uploaded successfully</div>}
          </div>

          <div className="file-upload-group">
            <label>
              Bank Statements (6 months):
              <span className="file-hint">Max 5MB</span>
            </label>
            <input
              type="file"
              accept=".pdf,image/jpeg,image/png"
              onChange={(e) => handleFileChange(e, "bank")}
              className={fileErrors.bank ? "file-error" : ""}
              disabled={isUploading}
            />
            {fileErrors.bank && <div className="field-error">{fileErrors.bank}</div>}
            {documentUploaded.bank && <div className="upload-success">✓ Uploaded successfully</div>}
          </div>

          <div className="file-upload-group">
            <label>
              Employment Certificate:
              <span className="file-hint">Max 5MB</span>
            </label>
            <input
              type="file"
              accept=".pdf,image/jpeg,image/png"
              onChange={(e) => handleFileChange(e, "employment")}
              className={fileErrors.employment ? "file-error" : ""}
              disabled={isUploading}
            />
            {fileErrors.employment && <div className="field-error">{fileErrors.employment}</div>}
            {documentUploaded.employment && <div className="upload-success">✓ Uploaded successfully</div>}
          </div>

          {isUploading && (
            <div className="upload-progress">
              <p>Uploading document...</p>
            </div>
          )}

          <button type="submit" className="submit-button" disabled={isUploading}>
            {isUploading ? "Uploading..." : "Submit Application"}
          </button>

          {successMessage && <div className="success-message">{successMessage}</div>}
          {error && <div className="error-message">{error}</div>}
        </form>
      </div>
    </div>
  )
}

export default Finance
